import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    title: '',
    email: '',
    inquiryType: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Contact',
        item: `${siteUrl}/contact`
      }
    ]
  };

  const contactPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Diivy',
    url: `${siteUrl}/contact`,
    description: 'Get in touch with Diivy for investor inquiries, partnership opportunities, or general questions about our portfolio companies.',
    mainEntity: {
      '@type': 'Organization',
      name: 'Diivy Medical Technology',
      url: siteUrl,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Investor Relations',
        availableLanguage: 'English',
        areaServed: 'Worldwide'
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) return;
    if (formData.message.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));

      const response = await fetch('https://readdy.ai/api/form/d6qdtlc4k19g20dvr8cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', title: '', email: '', inquiryType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-0 py-4 border-0 border-b-2 border-gray-200 bg-transparent text-[15px] text-[#0D0D0D] focus:outline-none focus:border-[#C9A96E] transition-all placeholder:text-gray-400";

  return (
    <>
      <SEOHead
        title="Contact Diivy | Investor Inquiries & Partnership Opportunities"
        description="Get in touch with Diivy for investor inquiries, partnership opportunities, or general questions about our portfolio companies. We welcome conversations with investors, operators, and strategic partners."
        keywords="contact Diivy, investor inquiries, partnership opportunities, business development, strategic partnerships, investor relations"
        canonical="/contact"
        ogTitle="Contact Diivy | Investor Inquiries & Partnership Opportunities"
        ogDescription="Get in touch with Diivy for investor inquiries, partnership opportunities, or general questions about our portfolio companies."
        ogUrl="/contact"
        twitterTitle="Contact Diivy | Investor Inquiries & Partnership Opportunities"
        twitterDescription="Get in touch with Diivy for investor inquiries, partnership opportunities, or general questions."
      />
      <JsonLd data={contactPageSchema} />
      <JsonLd data={breadcrumbSchema} />
      
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="pt-48 pb-32 bg-white">
          <div className="max-w-[680px] mx-auto px-8">
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-12 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#C9A96E]">
                Get in Touch
              </p>
            </div>

            <h1 className="text-[56px] lg:text-[64px] font-light text-[#0D0D0D] mb-10 leading-[1.1] tracking-[-0.02em]">
              Start a <span className="font-semibold">Conversation</span>
            </h1>

            <p className="text-[17px] text-gray-600 leading-[1.7] font-light">
              Whether you're an investor exploring opportunities, a partner seeking collaboration, or simply curious about our portfolio — we'd be delighted to hear from you.
            </p>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-32 bg-[#F7F4EF]">
          <div className="max-w-[680px] mx-auto px-8">

            {/* Gold accent top bar */}
            <div className="w-16 h-[2px] bg-[#C9A96E] mb-16"></div>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-10">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0D0D0D]/40 mb-4">
                  Full Name *
                </label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  className={inputClass} 
                  placeholder="Enter your full name" 
                />
              </div>

              {/* Company + Title row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="company" className="block text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0D0D0D]/40 mb-4">
                    Company
                  </label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company" 
                    value={formData.company} 
                    onChange={handleChange} 
                    className={inputClass} 
                    placeholder="Your organization" 
                  />
                </div>
                <div>
                  <label htmlFor="title" className="block text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0D0D0D]/40 mb-4">
                    Title
                  </label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title" 
                    value={formData.title} 
                    onChange={handleChange} 
                    className={inputClass} 
                    placeholder="Your role" 
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0D0D0D]/40 mb-4">
                  Email Address *
                </label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  className={inputClass} 
                  placeholder="your.email@company.com" 
                />
              </div>

              {/* Inquiry Type */}
              <div>
                <label htmlFor="inquiryType" className="block text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0D0D0D]/40 mb-4">
                  Nature of Inquiry
                </label>
                <select 
                  id="inquiryType" 
                  name="inquiryType" 
                  value={formData.inquiryType} 
                  onChange={handleChange} 
                  className={`${inputClass} cursor-pointer`}
                >
                  <option value="">Select an option</option>
                  <option value="investor">Investor Relations</option>
                  <option value="partnership">Strategic Partnership</option>
                  <option value="portfolio">Portfolio Inquiry</option>
                  <option value="sector">Sector Opportunity</option>
                  <option value="other">General Inquiry</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-[10px] font-semibold tracking-[0.2em] uppercase text-[#0D0D0D]/40 mb-4">
                  Your Message *
                </label>
                <textarea 
                  id="message" 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  required 
                  maxLength={500} 
                  rows={6} 
                  className={`${inputClass} resize-none`} 
                  placeholder="Share details about your inquiry..." 
                />
                <p className="text-[11px] text-[#0D0D0D]/30 mt-3 tracking-wide font-light">
                  {formData.message.length} / 500 characters
                </p>
              </div>

              {/* Submit */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-10 py-5 bg-[#0A0F1E] text-white text-[13px] font-medium tracking-[0.05em] hover:bg-[#C9A96E] hover:text-[#0A0F1E] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending Message...' : 'Send Message'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="p-6 border-l-[3px] border-[#C9A96E] bg-[#C9A96E]/5">
                  <p className="text-[14px] text-[#0D0D0D]/70 font-light leading-relaxed">
                    Thank you for reaching out. A member of our team will respond within 24–48 hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-6 border-l-[3px] border-red-400 bg-red-50">
                  <p className="text-[14px] text-red-700 font-light leading-relaxed">
                    We encountered an issue submitting your message. Please try again or contact us directly.
                  </p>
                </div>
              )}
            </form>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}